import { openSnackbar } from 'store/slices/snackbar';
import { dispatch } from 'store';
import { AnyMessageParams } from 'yup/lib/types';
import { isArray, map, compact, find, round } from 'lodash';
import moment from 'moment';
import { TradingAccountStatus } from 'types/accounts';
import { Theme } from '@mui/material';
import { TFunction } from 'i18next';
export const notifyErrors = (errors: any, objectName = '') => {
    if (!errors) {
        return null;
    }
    if (errors) {
        if (objectName) {
            errors = (function (e) {
                return (e && e.error) || [];
            })(find(errors, { key: objectName }));
        }
        if (!errors.length) {
            if (Object.keys(errors).length) {
                Object.keys(errors).forEach((param) => {
                    let err = errors[param];
                    dispatch(
                        openSnackbar({
                            open: true,
                            message: err,
                            variant: 'alert',
                            alert: {
                                color: 'error'
                            },
                            autoHideDuration: 5000,
                            disableWindowBlurListener: true
                        })
                    );
                });
            }
            return null;
        }
        if (typeof errors == 'string') {
            errors = map(compact(errors.split('.')), (e, i) => {
                return { key: objectName + i, error: e.trim() + '.' };
            });
        }
        errors
            .filter((e: any) => !Array.isArray(e))
            .forEach((err: AnyMessageParams) => {
                let errMsg;
                if (isArray(err.error)) {
                    errMsg = err.error.map((e) => e.error).join(' ');
                } else {
                    errMsg = err.error;
                }
                dispatch(
                    openSnackbar({
                        open: true,
                        variant: 'alert',
                        message: errMsg,
                        alert: {
                            color: 'error'
                        }
                    })
                );
            });

        return null;
    }
};
export const notifySuccess = (successMessage: string) => {
    dispatch(
        openSnackbar({
            open: true,
            message: successMessage,
            variant: 'alert',
            alert: {
                color: 'success'
            },
            autoHideDuration: 5000,
            disableWindowBlurListener: true
        })
    );
};

export function Pluralize(str: string, count = 2) {
    if (!str) return '';

    if (count == 1) return str;

    if (str[str.length - 1] == 'y') {
        return str.substring(0, str.length - 1) + 'ies';
    } else {
        return str ? str + 's' : '';
    }
}

export enum ComplianceStatus {
    Approved = 'approved',
    Pending = 'pending',
    InVerification = 'in_verification',
    Rejected = 'rejected'
}

export const simplifyNumber = (number: number, scale = 2, skipThousands = false, avoidRound = false) => {
    const units = ['', 'K', 'M', 'B', 'T'];
    const log10 = Math.floor(Math.log10(Math.abs(number)));
    // Handle numbers less than 1
    if (log10 < 0) {
        return number.toFixed(scale);
    }
    const unitIndex = Math.floor(Math.log10(Math.abs(number)) / 3);
    //if skipThousands==true, only numbers mith more than 6 digits will be simplified
    if (skipThousands && log10 < 5) {
        return number;
    }

    let simplifiedNumber = (number / Math.pow(1000, unitIndex)).toFixed(scale);
    let unit = units[unitIndex];
    if (unitIndex >= units.length) {
        simplifiedNumber = (number / Math.pow(1000, units.length - 1)).toFixed(scale);
        unit = units[units.length - 1];
    }
    if (avoidRound) {
        simplifiedNumber = (number / Math.pow(1000, unitIndex)).toString();
        const decimalIndex = simplifiedNumber.indexOf('.');
        if (decimalIndex !== -1) {
            const decimalPart = simplifiedNumber.slice(decimalIndex + 1);
            simplifiedNumber = decimalPart.length > scale ? simplifiedNumber.slice(0, decimalIndex + scale + 1) : simplifiedNumber;
        }
    }
    return simplifiedNumber + unit;
};

export function timeUntilFutureDate(futureDate: Date) {
    const now = moment();
    const futureDateTime = moment(futureDate);

    const duration = moment.duration(futureDateTime.diff(now));

    const days = duration.days();
    const hours = duration.hours();
    const minutes = duration.minutes();
    const seconds = duration.seconds();

    if (days > 0) {
        return `${days} day${days !== 1 ? 's' : ''}`;
    } else if (hours > 0) {
        return `${hours} hour${hours !== 1 ? 's' : ''}`;
    } else if (minutes > 0) {
        return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
    }
    return `${seconds} second${seconds !== 1 ? 's' : ''}`;
}

export function timeAgo(pastDateTime: Date, t: TFunction) {
    const now = new Date();

    const timeDifference = now.getTime() - pastDateTime.getTime();

    const seconds = Math.floor(timeDifference / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const months = Math.floor(days / 30);
    const years = Math.floor(months / 12);

    if (years > 0) {
        return years === 1 ? t('common.n_years_ago', { n: years }) : t('common.n_years_ago_plural', { n: years });
    } else if (months > 0) {
        return months === 1 ? t('common.n_months_ago', { n: months }) : t('common.n_months_ago_plural', { n: months });
    } else if (days > 0) {
        return days === 1 ? t('common.n_days_ago', { n: days }) : t('common.n_days_ago_plural', { n: days });
    } else if (hours > 0) {
        return hours === 1 ? t('common.n_hours_ago', { n: hours }) : t('common.n_hours_ago_plural', { n: hours });
    } else if (minutes > 0) {
        return minutes === 1 ? t('common.n_minutes_ago', { n: minutes }) : t('common.n_minutes_ago_plural', { n: minutes });
    } else {
        return seconds === 1 ? t('common.n_seconds_ago', { n: seconds }) : t('common.n_seconds_ago_plural', { n: seconds });
    }
}

export function getHoursSince(dateString: string): number {
    const date = moment(dateString);
    const hoursDifference = moment().diff(date, 'hours');
    return hoursDifference;
}
export function formatDuration(seconds: number, t: TFunction): string {
    const SECONDS_IN_A_MINUTE = 60;
    const SECONDS_IN_AN_HOUR = 3600;
    const SECONDS_IN_A_DAY = 86400;

    const days = Math.floor(seconds / SECONDS_IN_A_DAY);
    seconds %= SECONDS_IN_A_DAY;

    const hours = Math.floor(seconds / SECONDS_IN_AN_HOUR);
    seconds %= SECONDS_IN_AN_HOUR;

    const minutes = Math.floor(seconds / SECONDS_IN_A_MINUTE);
    const secs = seconds % SECONDS_IN_A_MINUTE;

    // Create the formatted string
    const parts = [];
    if (days > 0) parts.push(t(`common.${days !== 1 ? 'n_days_plural' : 'n_days'}`, { n: days }));
    if (hours > 0) parts.push(t(`common.${hours !== 1 ? 'n_hours_plural' : 'n_hours'}`, { n: hours }));
    if (minutes > 0) parts.push(t(`common.${minutes !== 1 ? 'n_minutes_plural' : 'n_minutes'}`, { n: minutes }));
    if (secs > 0 || parts.length === 0) parts.push(t(`common.${secs !== 1 ? 'n_seconds_plural' : 'n_seconds'}`, { n: secs }));

    return parts.join(', ');
}

export function roundPercentage(value: number, precission = 2) {
    let v = round(value, precission);
    if (value > 0 && value < 1 && v == 0) {
        //increment precission for values between 0-1
        v = round(value, precission + 2);
    }
    return v;
}

export const getAccountStatusColor = (theme: Theme, status: TradingAccountStatus) => {
    switch (status) {
        case TradingAccountStatus.ACTIVE:
        case TradingAccountStatus.PASSED:
            return theme.palette.success.main;
        case TradingAccountStatus.SUSPENDED:
            return theme.palette.orange.main;
        case TradingAccountStatus.CANCELLED:
        case TradingAccountStatus.FAILED:
        case TradingAccountStatus.DELETED:
            return theme.palette.error.main;
        case TradingAccountStatus.IN_PROGRESS:
            return '#2196F3';
        default:
            return '#9E9E9E';
    }
};
